/**
 * Route-level loading states.
 *
 * Every layout and page has a skeleton shaped like the screen it stands in
 * for, so content settles into place instead of popping in: static copy
 * (titles, table headers, tabs) renders for real and only data is stubbed out.
 *
 * Pending screens nest. A layout's skeleton renders a `PendingOutlet` where its
 * `<Outlet />` would be, which shows the skeleton of the next route being
 * loaded — so a cold load of an application tab paints the shell, the
 * application header and that tab's skeleton in one frame, and each layer
 * swaps for the real thing as its data lands.
 */
import { Heading, Inline, Logo, PageHeader, Panel, Skeleton, Stack, surface, Table, TBody, Td, Th, THead, Tr } from "@nudgebox/ui";
import "@nudgebox/ui/tokens.stylex";
import { color, media, radius, space } from "@nudgebox/ui/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import { useQuery } from "@tanstack/react-query";
import { type AnyRoute, useMatch, useParams, useRouter, useRouterState } from "@tanstack/react-router";
import { createContext, type ReactNode, useContext, useEffect, useState } from "react";
import { SettingsHeader } from "@/features/settings/SettingsHeader.tsx";
import { organizationQuery } from "@/lib/queries.ts";

/* ------------------------------------------------------------------------ */
/* Nesting                                                                  */
/* ------------------------------------------------------------------------ */

/** Route id of the pending screen being rendered, when it was nested by a parent's `PendingOutlet`. */
const PendingRouteContext = createContext<string | undefined>(undefined);

/**
 * Stand-in for `<Outlet />` inside a layout's pending screen: renders the
 * pending screen of the next route in the lane, or `fallback` when it has none.
 */
export function PendingOutlet({
  fallback = <ContentPending />
}: {
  fallback?: ReactNode;
}) {
  const router = useRouter();
  const ownRouteId = useMatch({
    strict: false,
    select: match => match.routeId
  });
  const routeId = useContext(PendingRouteContext) ?? ownRouteId;
  const nextRouteId = useRouterState({
    select: state => {
      const index = state.matches.findIndex(match => match.routeId === routeId);
      return index === -1 ? undefined : state.matches[index + 1]?.routeId;
    }
  });
  const Pending = nextRouteId ? (router.routesById as Record<string, AnyRoute>)[nextRouteId]?.options.pendingComponent : undefined;
  if (!nextRouteId || !Pending) return fallback;
  return <PendingRouteContext.Provider value={nextRouteId}>
      <Pending />
    </PendingRouteContext.Provider>;
}

/* ------------------------------------------------------------------------ */
/* App boot                                                                 */
/* ------------------------------------------------------------------------ */

/**
 * Full-page boot screen while the session and first route load. Mirrors the
 * static one in `index.html`, so the hand-off from HTML to React is invisible.
 */
export function AppPending() {
  return <output aria-label="Loading Nudgebox" className="xixxii4 x10a8y8t x78zum5 xdt5ytf x6s0dn4 xl56j7k x18w2rdc xvccuex xqcmdr3 x5hsz1j">
      <span className="x78zum5 x1iezwvt x1aquc0h x18hd8ej x4hg4is xa4qsjk">
        <Logo compact size={40} />
      </span>
      <span className="x1n2onr6 x1lliihq x13oubkp xuoj239 xb3r6kr x257jf6 xyez3s8">
        <span className="x10l6tqk x1lliihq x10no89f xu96u03 xz84dc7 x257jf6 xo3mcg6 x1a44k40 x1aquc0h xr7ufhj xq57n9 xa4qsjk" />
      </span>
    </output>;
}

/** Pathless layouts with nothing of their own to show: defer to the route below. */
export function PassthroughPending() {
  return <PendingOutlet fallback={<AppPending />} />;
}

/* ------------------------------------------------------------------------ */
/* Navigation progress                                                      */
/* ------------------------------------------------------------------------ */

type ProgressPhase = "idle" | "loading" | "done";

/**
 * A thin accent bar along the top of the viewport while the router loads the
 * next page. Quick navigations (warm cache, preloaded on hover) never show it.
 */
export function NavigationProgress() {
  const loading = useRouterState({
    select: state => state.isLoading
  });
  const [phase, setPhase] = useState<ProgressPhase>("idle");
  const [wasLoading, setWasLoading] = useState(loading);

  // A load that finishes after the bar appeared runs it to the end.
  if (loading !== wasLoading) {
    setWasLoading(loading);
    if (!loading && phase === "loading") setPhase("done");
  }
  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => setPhase("loading"), 120);
    return () => clearTimeout(timer);
  }, [loading]);
  useEffect(() => {
    if (phase !== "done") return;
    const timer = setTimeout(() => setPhase("idle"), 450);
    return () => clearTimeout(timer);
  }, [phase]);
  return <div aria-hidden className="xixxii4 x13vifvy x17y0mx6 x1fina04 x36qwtl x47corl">
      <div {...stylex.props(styles.progressBar, progressPhases[phase])} />
    </div>;
}

/* ------------------------------------------------------------------------ */
/* Building blocks                                                          */
/* ------------------------------------------------------------------------ */

/** Widths cycled through for rows so a skeleton list looks written, not stamped. */
const ROW_WIDTHS = ["72%", "54%", "86%", "61%", "78%", "47%", "66%"];
const rowWidth = (index: number, offset = 0) => ROW_WIDTHS[(index + offset) % ROW_WIDTHS.length];
function ButtonPending({
  width = 132
}: {
  width?: number;
}) {
  return <Skeleton width={width} height={34} radius="md" />;
}
function PageHeaderPending({
  title,
  description,
  actions = 0
}: {
  /** Static titles render for real; omit for data-driven ones. */
  title?: ReactNode;
  description?: ReactNode;
  actions?: number;
}) {
  return <PageHeader title={title ?? <Skeleton width={220} height={24} />} description={description ?? <Skeleton width={320} />} actions={actions > 0 ? Array.from({
    length: actions
  }, (_, index) => <ButtonPending key={index} width={index ? 104 : 132} />) : null} />;
}
function MetricPending({
  index
}: {
  index: number;
}) {
  return <Stack gap="xs">
      <Skeleton width={[112, 136, 148, 96][index % 4]} height={12} />
      <Skeleton width={[64, 88, 72, 56][index % 4]} height={29} />
      <Skeleton width={[148, 120, 164, 132][index % 4]} height={13} />
    </Stack>;
}

/** A raised strip of operational numbers, like the overview metrics. */
function MetricsPending({
  count
}: {
  count: 2 | 3 | 4;
}) {
  const columns = {
    2: metricColumns.two,
    3: metricColumns.three,
    4: metricColumns.four
  }[count];
  return <div {...stylex.props(surface.raised, surface.md, styles.metrics, columns)}>
      {Array.from({
      length: count
    }, (_, index) => <MetricPending key={index} index={index} />)}
    </div>;
}
type CellKind = "text" | "title" | "avatar" | "badge" | "mono" | "number" | "action";
export interface PendingColumn {
  label?: string;
  kind?: CellKind;
  align?: "left" | "right";
  width?: number;
}
function CellPending({
  kind = "text",
  row,
  column
}: {
  kind?: CellKind;
  row: number;
  column: number;
}) {
  switch (kind) {
    case "title":
      return <Stack gap="xs">
          <Skeleton width={rowWidth(row, column)} height={13} />
          <Skeleton width={rowWidth(row, column + 3)} height={11} />
        </Stack>;
    case "avatar":
      return <Inline gap="md">
          <Skeleton width={28} height={28} radius="md" />
          <Stack gap="xs" grow>
            <Skeleton width={rowWidth(row, column)} height={13} />
            <Skeleton width={rowWidth(row, column + 2)} height={11} />
          </Stack>
        </Inline>;
    case "badge":
      return <Skeleton width={[64, 76, 58][row % 3]} height={20} radius="full" />;
    case "mono":
      return <Skeleton width={[148, 120, 136][row % 3]} height={12} />;
    case "number":
      return <Inline justify="end">
          <Skeleton width={[44, 64, 36, 52][row % 4]} height={12} />
        </Inline>;
    case "action":
      return <Inline justify="end">
          <Skeleton width={28} height={28} radius="md" />
        </Inline>;
    default:
      return <Skeleton width={rowWidth(row, column)} height={12} />;
  }
}

/** A real table frame and header with stubbed-out rows. */
function TablePending({
  columns,
  rows = 5
}: {
  columns: PendingColumn[];
  rows?: number;
}) {
  return <Table>
      <THead>
        <tr>
          {columns.map((column, index) => <Th key={column.label ?? index} align={column.align} width={column.width}>
              {column.label}
            </Th>)}
        </tr>
      </THead>
      <TBody>
        {Array.from({
        length: rows
      }, (_, row) => <Tr key={row}>
            {columns.map((column, index) => <Td key={column.label ?? index} align={column.align}>
                <CellPending kind={column.kind} row={row} column={index} />
              </Td>)}
          </Tr>)}
      </TBody>
    </Table>;
}
function FieldPending({
  index
}: {
  index: number;
}) {
  return <Stack gap="sm">
      <Skeleton width={[96, 72, 120, 84][index % 4]} height={12} />
      <Skeleton height={34} radius="md" />
    </Stack>;
}

/** A raised panel with form fields (or text lines) and a footer action. */
function PanelPending({
  title,
  fields = 2,
  lines = 0,
  footer = true
}: {
  title?: string;
  fields?: number;
  lines?: number;
  footer?: boolean;
}) {
  return <Panel title={title ?? <Skeleton width={140} height={14} />} description={<Skeleton width={260} height={12} />} footer={footer ? <ButtonPending width={112} /> : undefined}>
      <Stack gap="lg">
        {Array.from({
        length: fields
      }, (_, index) => <FieldPending key={index} index={index} />)}
        {Array.from({
        length: lines
      }, (_, index) => <Skeleton key={index} width={rowWidth(index, 2)} />)}
      </Stack>
    </Panel>;
}
function CodePending({
  lines = 6
}: {
  lines?: number;
}) {
  return <div {...stylex.props(surface.raised, surface.md, surface.plain, styles.code)}>
      <Inline gap="md">
        <Skeleton width={52} height={12} />
        <Skeleton width={44} height={12} />
        <Skeleton width={60} height={12} />
      </Inline>
      <Stack gap="sm">
        {Array.from({
        length: lines
      }, (_, index) => <Skeleton key={index} width={rowWidth(index, 1)} height={12} />)}
      </Stack>
    </div>;
}
function Section({
  heading,
  children
}: {
  heading: ReactNode;
  children: ReactNode;
}) {
  return <Stack gap="md">
      {typeof heading === "string" ? <Heading level={2}>{heading}</Heading> : heading}
      {children}
    </Stack>;
}
function Busy({
  children
}: {
  children: ReactNode;
}) {
  return <div aria-busy className="xeuugli">
      {children}
    </div>;
}

/** Generic page content: used where a route has no skeleton of its own. */
export function ContentPending() {
  return <Busy>
      <PageHeaderPending />
      <Stack gap="xxl">
        <PanelPending fields={0} lines={3} footer={false} />
        <PanelPending fields={2} footer={false} />
      </Stack>
    </Busy>;
}

/* ------------------------------------------------------------------------ */
/* Organization pages                                                       */
/* ------------------------------------------------------------------------ */

const NOTIFICATION_COLUMNS: PendingColumn[] = [{
  label: "Title",
  kind: "title"
}, {
  label: "Status",
  kind: "badge"
}, {
  label: "Delivered",
  kind: "number",
  align: "right"
}, {
  label: "Date",
  kind: "number",
  align: "right"
}];
export function OverviewPending() {
  const {
    orgSlug = ""
  } = useParams({
    strict: false
  });
  // The shell loaded the organization, so its name is usually already here.
  const {
    data: organization
  } = useQuery({
    ...organizationQuery(orgSlug),
    enabled: Boolean(orgSlug)
  });
  return <Busy>
      <PageHeaderPending title={organization?.name} description="Your notification infrastructure at a glance." />
      <Stack gap="x3">
        <MetricsPending count={3} />
        <Section heading="Recent notifications">
          <TablePending columns={NOTIFICATION_COLUMNS} rows={4} />
        </Section>
      </Stack>
    </Busy>;
}
export function ApplicationsPending() {
  return <Busy>
      <PageHeaderPending title="Applications" description="Each application has its own subscribers, channels and public subscription link." actions={1} />
      <TablePending columns={[{
      label: "Application",
      kind: "avatar"
    }, {
      label: "Website"
    }, {
      label: "Subscribers",
      kind: "number",
      align: "right"
    }, {
      label: "Subscription link",
      kind: "mono"
    }, {
      label: "Created",
      kind: "number",
      align: "right"
    }]} rows={3} />
    </Busy>;
}
export function ApiKeysPending() {
  return <Busy>
      <PageHeaderPending title="API keys" description="Secret keys your backend uses to send notifications. Keep them server-side — never in a browser or mobile app." actions={1} />
      <TablePending columns={[{
      label: "Name",
      kind: "title"
    }, {
      label: "Key",
      kind: "mono"
    }, {
      label: "Access",
      kind: "badge"
    }, {
      label: "Created",
      kind: "number",
      align: "right"
    }, {
      label: "Last used",
      kind: "number",
      align: "right"
    }, {
      kind: "action",
      width: 96
    }]} rows={3} />
    </Busy>;
}
export function DevelopersPending() {
  return <Busy>
      <PageHeaderPending title="Documentation" description="Everything you need to send your first notification from a backend." />
      <Stack gap="x3">
        <Section heading="Send a notification">
          <Skeleton width="64%" />
          <CodePending lines={8} />
        </Section>
        <Section heading="Endpoints">
          <TablePending columns={[{
          label: "Method",
          kind: "badge",
          width: 110
        }, {
          label: "Path",
          kind: "mono"
        }, {
          label: "Auth",
          kind: "badge"
        }, {
          label: "Description"
        }]} rows={4} />
        </Section>
      </Stack>
    </Busy>;
}
export function MembersPending() {
  return <Busy>
      <PageHeaderPending title="Members" description="People with access to this organization." />
      <TablePending columns={[{
      label: "Member",
      kind: "avatar"
    }, {
      label: "Role",
      kind: "badge"
    }, {
      label: "Joined",
      kind: "number",
      align: "right"
    }]} rows={3} />
    </Busy>;
}
export function BillingPending() {
  return <Busy>
      <PageHeaderPending title="Billing" description="Your plan, usage and payment details. Payments are handled by Polar." />
      <Stack gap="xxl">
        <PanelPending fields={0} lines={3} />
        <MetricsPending count={2} />
        <PanelPending fields={0} lines={2} footer={false} />
      </Stack>
    </Busy>;
}
export function OrganizationSettingsPending() {
  return <Busy>
      <SettingsHeader description="Organization details, shared by every member." />
      <Stack gap="xxl">
        <PanelPending title="General" fields={2} />
        <PanelPending title="Details" fields={0} lines={3} footer={false} />
      </Stack>
    </Busy>;
}
export function AccountSettingsPending() {
  return <Busy>
      <SettingsHeader description="Your own account, across every organization you belong to." />
      <Stack gap="xxl">
        <PanelPending title="Profile" fields={0} lines={3} footer={false} />
        <PanelPending title="Passkeys" fields={0} lines={2} />
      </Stack>
    </Busy>;
}

/* ------------------------------------------------------------------------ */
/* Application tabs                                                         */
/* ------------------------------------------------------------------------ */

/** Content below the application header when a tab has no skeleton of its own. */
export function TabPending() {
  return <Busy>
      <Stack gap="xxl">
        <PanelPending fields={0} lines={3} footer={false} />
        <PanelPending fields={2} footer={false} />
      </Stack>
    </Busy>;
}
export function ApplicationOverviewPending() {
  return <Busy>
      <div className="xrvj5dj xlghu0s xesxnj2 xxyh0xd x7a106z">
        <Stack gap="x3">
          <MetricsPending count={2} />
          <Section heading="Recent notifications">
            <TablePending columns={NOTIFICATION_COLUMNS} rows={4} />
          </Section>
        </Stack>
        <Stack gap="lg">
          <Panel title={<Skeleton width={120} height={14} />}>
            <Stack gap="lg" align="center">
              <Skeleton width={168} height={168} radius="lg" />
              <Skeleton height={34} radius="md" />
            </Stack>
          </Panel>
          <PanelPending fields={0} lines={4} footer={false} />
        </Stack>
      </div>
    </Busy>;
}
export function NotificationsPending() {
  return <Busy>
      <Stack gap="lg">
        <Inline>
          <ButtonPending width={196} />
        </Inline>
        <Inline gap="lg">
          <Skeleton width={56} height={14} />
          <Skeleton width={72} height={14} />
        </Inline>
        <NotificationsListPending />
      </Stack>
    </Busy>;
}

/** The notifications table alone, for in-page loads such as switching tabs. */
export function NotificationsListPending() {
  return <TablePending columns={[{
    label: "Title",
    kind: "title"
  }, {
    label: "Audience"
  }, {
    label: "Status",
    kind: "badge"
  }, {
    label: "Delivered",
    kind: "number",
    align: "right"
  }, {
    label: "Success",
    kind: "number",
    align: "right"
  }, {
    label: "Date",
    kind: "number",
    align: "right"
  }]} rows={6} />;
}
export function ComposerPending() {
  return <Busy>
      <div className="xrvj5dj xxyh0xd x7a106z x12gnqk0 xesxnj2">
        <PanelPending fields={4} />
        <Stack gap="md" align="center">
          <div {...stylex.props(surface.raised, surface.lg, styles.phone)}>
            <Stack gap="sm">
              <Inline gap="sm">
                <Skeleton width={20} height={20} radius="sm" />
                <Skeleton width={96} height={12} />
              </Inline>
              <Skeleton width="80%" height={13} />
              <Skeleton width="92%" height={12} />
              <Skeleton width="56%" height={12} />
            </Stack>
          </div>
        </Stack>
      </div>
    </Busy>;
}
export function NotificationDetailPending() {
  return <Busy>
      <Stack gap="x3">
        <Stack gap="sm">
          <Inline gap="md">
            <Skeleton width={260} height={24} />
            <Skeleton width={72} height={20} radius="full" />
          </Inline>
          <Skeleton width={420} />
        </Stack>
        <MetricsPending count={4} />
        <div className="xrvj5dj xxyh0xd x7a106z x1lvdbfn xesxnj2">
          <Section heading="Delivery attempts">
            <DeliveriesListPending />
          </Section>
          <Section heading="Details">
            <PanelPending fields={0} lines={5} footer={false} />
          </Section>
        </div>
      </Stack>
    </Busy>;
}

/** The delivery attempts table alone, loaded after the notification itself. */
export function DeliveriesListPending() {
  return <TablePending columns={[{
    label: "Delivery",
    kind: "mono"
  }, {
    label: "Status",
    kind: "badge"
  }, {
    label: "Platform"
  }, {
    label: "Provider"
  }, {
    label: "Error"
  }]} rows={4} />;
}
export function SubscribersPending() {
  return <Busy>
      <Stack gap="lg">
        <Stack gap="xs">
          <Skeleton width="88%" />
          <Skeleton width="52%" />
        </Stack>
        <Inline gap="sm">
          <ButtonPending width={148} />
          <ButtonPending width={148} />
        </Inline>
        <SubscribersListPending />
      </Stack>
    </Busy>;
}

/** The subscribers table alone, for in-page loads such as changing filters. */
export function SubscribersListPending() {
  return <TablePending columns={[{
    label: "Subscription",
    kind: "mono"
  }, {
    label: "Channel"
  }, {
    label: "Status",
    kind: "badge"
  }, {
    label: "Platform"
  }, {
    label: "Subscribed",
    kind: "number",
    align: "right"
  }, {
    label: "Last activity",
    kind: "number",
    align: "right"
  }]} rows={6} />;
}
export function ChannelsPending() {
  return <Busy>
      <Stack gap="lg">
        <Inline justify="between" align="start">
          <Skeleton width="56%" />
          <ButtonPending width={120} />
        </Inline>
        <TablePending columns={[{
        label: "Channel",
        kind: "title"
      }, {
        label: "Slug",
        kind: "mono"
      }, {
        label: "Subscribers",
        kind: "number",
        align: "right"
      }, {
        label: "Subscription link"
      }, {
        kind: "action",
        width: 48
      }]} rows={3} />
      </Stack>
    </Busy>;
}
const REDUCED_MOTION = "@media (prefers-reduced-motion: reduce)";
const slide = "x1do5dr1-B";
const fadeIn = "x18re5ia-B";
const bob = "x1n4ae4f-B";
const styles = {
  progressBar: {
    kZKoxP: "x5yr21d",
    k3nNDw: "x19w6rv",
    kWkggS: "xo3mcg6",
    kGVxlE: "xq7pqek",
    ksF3WI: "x14gstfv",
    kYm2EN: "x1berhj5",
    $$css: true
  },
  metrics: {
    k1xSpc: "xrvj5dj",
    kOIVth: "x9p7km9 x1y6lc4a",
    k8WAf4: "x1ybrkpx",
    kg3NbH: "xh1le5o x1lavhwh",
    kaIpWk: "xlfxrz8",
    $$css: true
  },
  code: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x4l6xbm",
    kmVPX3: "x1v448bx",
    kaIpWk: "xlfxrz8",
    $$css: true
  },
  phone: {
    kzqmXN: "xh8yej3",
    ks0D6T: "x1va8c73",
    kmVPX3: "x1v448bx",
    kaIpWk: "x16l8lxw",
    $$css: true
  }
};

/*
 * The bar crawls toward 85% on a long, decelerating transition while the page
 * loads, snaps to full when it lands, then fades and resets while invisible.
 */
const progressPhases = {
  idle: {
    kSiTet: "xg01cxk",
    k3aq6I: "x1ufz0iv",
    k1ekBW: "x13b0p5u",
    $$css: true
  },
  loading: {
    kSiTet: "x1hc1fzr",
    k3aq6I: "xbk8vjh",
    k1ekBW: "xwcsmn1",
    kIyJzY: "x1n5g8cs",
    kAMwcw: "x1iewtxe",
    $$css: true
  },
  done: {
    kSiTet: "xg01cxk",
    k3aq6I: "xzn2k1w",
    k1ekBW: "xwcsmn1",
    kIyJzY: "x1k00tv1",
    kIr0Dl: "x1lgl8fz",
    kAMwcw: "xn34ekc",
    $$css: true
  }
};
const metricColumns = {
  two: {
    kumcoG: "xnby9oq",
    $$css: true
  },
  three: {
    kumcoG: "x185a7wo xpo9xo4",
    $$css: true
  },
  four: {
    kumcoG: "x1sipb5g x1mu2h51",
    $$css: true
  }
};